import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Button, Card, Container, Row, Col, Alert, ListGroup } from 'react-bootstrap';
import { publicApi } from '../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const PublicBookingPage = () => {
  const { businessId } = useParams();
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    notes: ''
  });

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        setLoading(true);
        const response = await publicApi.getBusiness(businessId);
        setBusiness(response.data.data.business);
        setServices(response.data.data.services);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load business information');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [businessId]);

  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    setSelectedTimeSlot(null); // Reset selected time slot
    setFormData(prev => ({
      ...prev,
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      notes: ''
    }));

    // Fetch availability for the selected service
    try {
      const serviceId = service.id || service._id;
      const dateStr = selectedDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      const response = await publicApi.getBusinessAvailability(businessId, {
        serviceId: serviceId,
        date: dateStr
      });
      setAvailableSlots(response.data.data.availableSlots);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load availability');
    }
  };

  const handleDateChange = async (date) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null); // Reset selected time slot when date changes

    if (selectedService) {
      try {
        const serviceId = selectedService.id || selectedService._id;
        const dateStr = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        const response = await publicApi.getBusinessAvailability(businessId, {
          serviceId: serviceId,
          date: dateStr
        });
        setAvailableSlots(response.data.data.availableSlots);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load availability');
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedService) {
      setError('Please select a service');
      return;
    }

    if (!selectedTimeSlot) {
      setError('Please select a time slot');
      return;
    }

    // Parse the selected time slot
    const [hours, minutes] = selectedTimeSlot.start.split(':');
    const startTime = new Date(selectedDate);
    startTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    try {
      const serviceId = selectedService.id || selectedService._id;
      const response = await publicApi.createPublic({
        businessId,
        serviceId: serviceId,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        startTime: startTime.toISOString(),
        notes: formData.notes
      });

      // Redirect to confirmation page with appointment details
      // In a real app, you might use state or URL params
      window.location.href = `/confirmation?appointmentId=${response.data.data.appointment._id}`;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create appointment');
    }
  };

  if (loading) {
    return <div>Loading business information...</div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="mb-4">
            <Card.Body>
              <Card.Title>{business?.name}</Card.Title>
              <Card.Subtitle className="mb-3 text-muted">{business?.description}</Card.Subtitle>
              <Card.Text>
                {business?.location?.address && (
                  <div>
                    <strong>Address:</strong> {business.location.address}, {business.location.city}
                  </div>
                )}
              </Card.Text>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <Card.Title>Select a Service</Card.Title>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {services.map(service => (
                  <ListGroup.Item
                    key={service._id}
                    action
                    active={selectedService?._id === service._id}
                    onClick={() => handleServiceSelect(service)}
                  >
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{service.name}</strong>
                        <div className="text-muted">
                          {service.duration} minutes
                          {service.price && ` - $${service.price}`}
                        </div>
                        <div className="text-muted">{service.description}</div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>

          {selectedService && (
            <>
              <Card className="mb-4">
                <Card.Header>
                  <Card.Title>Select Date and Time</Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="mb-4">
                    <Form.Label className="mb-3 d-block" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      Date
                    </Form.Label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={handleDateChange}
                      className="form-control"
                      minDate={new Date()}
                      inline
                    />
                  </div>

                  <div className="mt-3">
                    <Form.Label className="mb-3 d-block" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      Available Time Slots
                    </Form.Label>
                    {availableSlots.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {availableSlots.map((slot, index) => {
                          const slotKey = `${slot.start}-${slot.end}`;
                          const isSelected = selectedTimeSlot && 
                            selectedTimeSlot.start === slot.start && 
                            selectedTimeSlot.end === slot.end;
                          return (
                            <Button
                              key={index}
                              variant={isSelected ? 'primary' : 'outline-primary'}
                              className="mb-2"
                              onClick={() => setSelectedTimeSlot(slot)}
                              style={{ minWidth: '120px' }}
                            >
                              {slot.start} - {slot.end}
                            </Button>
                          );
                        })}
                      </div>
                    ) : (
                      <Alert variant="warning">No available time slots for this date</Alert>
                    )}
                    {selectedTimeSlot && (
                      <Alert variant="info" className="mt-3">
                        Selected time: <strong>{selectedTimeSlot.start} - {selectedTimeSlot.end}</strong>
                      </Alert>
                    )}
                  </div>
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <Card.Title>Your Information</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="customerName"
                        value={formData.customerName}
                        onChange={handleFormChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="customerEmail"
                        value={formData.customerEmail}
                        onChange={handleFormChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="customerPhone"
                        value={formData.customerPhone}
                        onChange={handleFormChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="notes"
                        value={formData.notes}
                        onChange={handleFormChange}
                        rows={3}
                      />
                    </Form.Group>

                    <Button variant="primary" type="submit" className="w-100">
                      Book Appointment
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default PublicBookingPage;
